import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useDFR } from '../context/DFRContext';
import { usePropsDataStore } from '../store/PropsDataStore';

import PolarAreaChart from '../charts/PolarAreaChart';
import { Icon, Text } from '@ui-component-library';

import VBLeaflet from '@vb-js-commons/new/vbLeaflet';
import { swapCoords } from '@vb-js-commons/new/leafletConfig';

const ChartContainer = styled.div`
    display: flex;
    justify-content: center;

    canvas {
        width: 100% !important;
        height: 100% !important;
        max-width: 450px;
        aspect-ratio: 1 / 1;
    }
`

const BulletPointList = styled.ul`
    padding-left: 1.4em;
    list-style-type: disc;
    margin-bottom: 1.4em;

    li {
        padding-left: .2em;

        &::marker {
            font-size: .5em;
        }
    }
`

const BoxRadius = styled.div`
    border-radius: 16px;
`

const WindroseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        datalabels: {
            display: false,
            rotation: 11.25
        },
        legend: {
            display: false,
            position: 'right',
            labels: {
                generateLabels: function(chart) {
                    const datasets = chart.data.datasets;
                    return datasets.map((dataset, i) => ({
                        text: dataset.label,
                        fillStyle: dataset.backgroundColor,
                        strokeStyle: dataset.borderColor,
                        lineWidth: dataset.borderWidth,
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i
                    }));
                }
            }
        },
        // These are the hover effect datapoints and what to show
        tooltip: {
            callbacks: {
                label: function(context) {
                    const label = context.dataset.label + ': ' || '';
                    const actualValue = context.dataset?.actualData[context.dataIndex] ? context.dataset?.actualData[context.dataIndex] : 0
                    return actualValue > 0 ? label + actualValue.toFixed(2) + '%' : null
                }
            }
        }
    },
    interaction: {
        // If you turn this off the location of pointing is a bit weird and not super UX friendly
        mode: 'point'
    },
    scales: {
        r: {
            startAngle: -11.25,
            stacked: true,
            ticks: {
                z: 1,
                stepSize: 5,
                backdropColor: 'rgba(255, 255, 255, 0.5)',
                callback: function (value) {
                    return value.toFixed(0) + '%'
                },
            },
            // These are the location of the directions (N, E, S, W)
            pointLabels: {
                display: true,
                centerPointLabels: true,
                font: {
                    size: 14,
                    weight: 'bold'
                }
            },
            // In case you want lines in the grid
            //angleLines: { display: true }
        }
    },
}

export default function WindAnalysis() {
    const DFRContext = useDFR();
    const { props } = usePropsDataStore();

    const dfrActiveProps = props.filter(p => p.active && p.dfr)

    const [propMapRefs, setPropMapRefs] = useState({})
    dfrActiveProps.map((p) => {
        let has_added = false;
        if(!propMapRefs.hasOwnProperty(p.ref)){
            propMapRefs[p.ref] = React.createRef();
            has_added=true;
        }
        if(has_added){
            setPropMapRefs(propMapRefs);
        }
    })

    const setMapContent = (ref) => {
        const classColorMap = {
            1: "#EA2F14",
            2: "#E6521F",
            3: "#FB9E3A",
            4: "#FCEF91",
        }
        if(DFRContext.windEdgeRisks[ref] && DFRContext.windEdgeRisks[ref].error) {
            console.log('error', DFRContext.windEdgeRisks[ref].error)
        } else if(DFRContext.windEdgeRisks[ref] && !DFRContext.windEdgeRisks[ref].error && propMapRefs[ref]?.current?.state?.map && props.find(p => p.ref == ref).border) {
            let map = propMapRefs[ref].current.state.map
            
            let border = L.featureGroup([])
            props.find(p => p.ref == ref).border.map(b => {
                let layer = L.polygon(swapCoords(b), {color: "#FFFFFF", fillOpacity: 0, interactive: false, type: 'polygon'})
                layer.addTo(border)
            })

            let geomFG = L.featureGroup([])
            DFRContext.windEdgeRisks[ref].data.forEach((data) => {

                const color = classColorMap[data.class]

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillOpacity: 1,
                    weight: 2,
                    type: data.geom.type.toLowerCase()
                }
                if(data.geom.type === 'Polygon') {
                    let layer = L.polygon(swapCoords(data.geom.coordinates), baseStyle)
                    layer.addTo(geomFG)
                } else if(data.geom.type == "MultiPolygon"){
                    const multiPolygon = {
                        type: "Feature",
                        geometry: data.geom
                    };
                    
                    let layer = L.geoJSON(multiPolygon, baseStyle)
                    layer.addTo(geomFG)
                }
                
            })
            propMapRefs[ref].current.setLayers(geomFG)
            propMapRefs[ref].current.setLayers(border)
            geomFG.addTo(map)
            border.addTo(map)
            map.fitBounds(geomFG.getBounds())
        } else {
            setTimeout(() => {
                setMapContent(ref);
            }, 100)
        }
    }

    useEffect(() => {
        dfrActiveProps.forEach((p) => {
            if(DFRContext.windEdgeRisks[p.ref] && propMapRefs[p.ref]){
                setMapContent(p.ref)
            }
        })
    }, [DFRContext.windEdgeRisks, propMapRefs, dfrActiveProps])

    const now = new Date()

    const fakeChartData = {labels: ['N', '', '', '', 'Ö', '', '', '', 'S', '', '', '', 'V', '', '', ''], datasets: []}
    const createChartData = data => {
        let total = {}
        let values = {}
        const labels = ['N', '', '', '', 'E', '', '', '', 'S', '', '', '', 'W', '', '', '']
        const datasets = Object.entries(data.data).map(i => {
            Object.entries(i[1]).map(v => {
                values[v[0]] = v[1]
                if(total[v[0]]) {
                    total[v[0]] = total[v[0]] + v[1]
                } else {
                    total[v[0]] = v[1]
                }
            })

            return {
                data: Object.values(total).map(t => (t/data.total)*100),
                label: i[0],
                actualData: Object.values(values).map(t => (t/data.total)*100),
                backgroundColor: DFRContext.windSpeeds.find(s => s.name == i[0]).color,
                borderWidth: 1,
                borderColor: 'rgba(50, 50, 50, 0.2)'
            }
        })

        return {
            labels: labels,
            datasets: datasets
        }
    }

    const loadingChart = (data, type, p) => {
        return <ChartContainer className='relative'>
            <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center'>
                
                {!data || !data[type] ? <>
                    <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" role="status" />
                    <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Hämtar...</Text>
                </> : <>
                    <div className='flex justify-center items-center rounded rounded-full bg-red-scarlet-50 mb-2' style={{height: '40px', width: '40px'}}>
                        <Icon name='CrossSimple' className="fill-red-scarlet-500" size="large" />
                    </div>
                    <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Hämtning misslyckades.</Text>
                    <div role='button' onClick={() => DFRContext.getWind(p.ref, p.data.centroid, type)}>
                        <Text variant="text-xs/leading-normal/semibold" className="text-green-pine-500">Försök igen.</Text>
                    </div>
                </>}
            </div>
            <PolarAreaChart data={fakeChartData} options={WindroseOptions} />
        </ChartContainer>
    }

    const chartWrapper = (data, type, title, prop) => {
        return <div className='w-full'>
            <BoxRadius className='bg-stone-50 p-4 mb-4'>
                <Text variant="text-xl/leading-normal/medium" className='text-center'>{title}</Text>

                {data && data[type] && !data[type].error ? <ChartContainer>
                        <PolarAreaChart data={createChartData(data[type])} options={WindroseOptions} />
                </ChartContainer> : loadingChart(data, type, prop)}
            </BoxRadius>
        </div>
    }

    const fetchRiskLevelContainer = ref => {
        const riskValue = DFRContext.weatherData[ref]?.historic?.Byvind?.max || undefined
        const riskLevel = !riskValue ? undefined : riskValue > 25 ? 'high' : riskValue > 15 ? 'medium' : 'low'

        const boxSettings = {
            low: { 
                icon: 'CheckFilled',
                bgColor: 'success-200', 
                title: 'Låg risk', 
                text: 'Låg risk för vindskador. Förhållandena är gynnsamma och stabila. Ingen omedelbar åtgärd krävs.' 
            },
            medium: {
                icon: 'WarningFilled',
                bgColor: 'warning-200',
                title: 'Måttlig risk',
                text: 'Måttlig risk för vindpåverkan. Förhållandena kräver ökad uppmärksamhet, särskilt vid utsatta träd eller i nygallrade bestånd. Var beredd att justera utomhusaktiviteter vid behov.'
            },
            high: {
                icon: 'DangerFilled',
                bgColor: 'danger-300',
                title: 'Hög risk',
                text: 'Hög risk för vindskador. Förhållandena är farliga och instabila med stor risk för att träd faller. Ingen vistelse i skogen rekommenderas. Agera försiktigt och följ officiella varningar.'
            }
        }

        const box = boxSettings[riskLevel] ? boxSettings[riskLevel] : undefined
        
        if(!box) {
            return <BoxRadius className='flex flex-col justify-center items-center bg-white mt-4 h-full'>
                <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin my-2" role="status" />
                <Text variant="text-base/leading-normal/medium" className='text-charcoal-500 mb-2'>Hämtar...</Text>
            </BoxRadius>
        } else {
            return <BoxRadius className={`bg-${box.bgColor} h-full mt-4`} style={{padding: '16px'}}>
                <div className='flex items-center mb-1'>
                    <Icon name={box.icon} className="fill-charcoal-500" size="large" />
                    <Text variant="text-lg/leading-normal/medium" className='text-charcoal-500 ml-1'>{box.title}</Text>
                </div>
                <Text variant="text-base/leading-normal/normal" className='text-charcoal-500'>{box.text}</Text>
            </BoxRadius>
        }
    }

    const propertyData = p => {
        return <>
            <div className='grid grid-cols-1 lg:grid-cols-2 mt-4 gap-7'>
                <div className='w-full mb-2 lg:col-span-2'>
                    <Text variant="text-xl/leading-normal/medium">Vindros över {p.designation}</Text>
                </div>

                {chartWrapper(DFRContext.wind[p.ref], 'month', '30 dagar', p)}

                {chartWrapper(DFRContext.wind[p.ref], 'years', 'Senaste 10 år', p)}
            </div>
            <BoxRadius className='bg-stone-50 p-4'>
                <div className="flex flex-wrap">
                    <div className="w-full mt-4">
                        <Text variant="text-lg/leading-normal/medium" lg="text-xl/leading-normal/medium">Utsatta kantzoner</Text>
                        <Text variant="text-base/leading-normal/normal" className='text-pebble-500'>Kartan visar kantzoner med varierande grad av vindexponering. Färgskalan illustrerar risken för påverkan, ju mörkare färg desto större vindkänslighet.</Text>
                            
                        <BoxRadius className='w-full mt-2 overflow-hidden' style={{height: '450px'}}>
                            {DFRContext.windEdgeRisks[p.ref] && !DFRContext.windEdgeRisks[p.ref].error ? 
                                <VBLeaflet ref={propMapRefs[p.ref]} id={`${p.ref}_map`} baseMap='Mapbox Satelite' /> : DFRContext.windEdgeRisks[p.ref] && DFRContext.windEdgeRisks[p.ref].error ?
                                <div className="flex items-center justify-center w-full h-full flex-col">
                                    <div className='flex justify-center items-center rounded rounded-full bg-red-scarlet-50 mb-2' style={{height: '40px', width: '40px'}}>
                                        <Icon name='CrossSimple' className="fill-red-scarlet-500" size="large" />
                                    </div>
                                    <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Hämtning misslyckades.</Text>
                                    <div role='button' onClick={() => DFRContext.getWindEdgeRisk(p.ref)}>
                                        <Text variant="text-xs/leading-normal/semibold" className="text-green-pine-500">Försök igen.</Text>
                                    </div>
                                </div> : 
                                <div className="flex items-center justify-center w-full h-full flex-col">
                                    <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" role="status" />
                                    <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Hämtar...</Text>
                                </div>}
                        </BoxRadius>
                    </div>
                </div>
            </BoxRadius>

            <BoxRadius className='bg-stone-50 p-4 mt-4' style={{marginBottom: '48px'}}>
                <div className='grid grid-cols-1 lg:grid-cols-3 mt-4 gap-7'>
                    <div className='w-full lg:col-span-1 flex flex-col'>
                        <Text variant="text-lg/leading-normal/medium" lg="text-xl/leading-normal/medium">Vindöversikt - 30 dagar</Text>
                        <Text variant="text-base/leading-normal/normal" className='text-pebble-500'>Uppdaterad senast: {now.toLocaleDateString('sv-SE')}</Text>
                        
                        <div className='flex flex-col justify-between flex-grow'>
                            <BoxRadius className='flex flex-col justify-center items-center bg-white py-4 lg:py-2 mt-4 h-full'>
                                <Icon name='WindSimple' className="fill-charcoal-500" size="xlarge" />
                                <Text variant="text-base/leading-normal/medium" className='text-pebble-500 my-2'>Högsta byvind</Text>
                                {DFRContext.weatherData[p.ref]?.historic?.Byvind?.max ? <Text variant="text-xl/leading-normal/medium" className='text-pebble-500'>{Number(DFRContext.weatherData[p.ref].historic.Byvind.max).toFixed(1)} m/s</Text> : <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin my-2" role="status" />}
                            </BoxRadius>

                            <BoxRadius className='flex flex-col justify-center items-center bg-white py-4 lg:py-2 mt-4 h-full'>
                                <Icon name='WindSimple' className="fill-charcoal-500" size="xlarge" />
                                <Text variant="text-base/leading-normal/medium" className='text-pebble-500 my-2'>Genomsnittligt vindstyrka</Text>
                                {DFRContext.weatherData[p.ref]?.historic?.Vindhastighet?.avg ? <Text variant="text-xl/leading-normal/medium" className='text-pebble-500'>{Number(DFRContext.weatherData[p.ref].historic.Vindhastighet.avg).toFixed(1)} m/s</Text> : <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin my-2" role="status" />}
                            </BoxRadius>

                            {fetchRiskLevelContainer(p.ref)}
                        </div>
                    </div>

                    <div className='w-full lg:col-span-2 mt-4 lg:mt-0'>
                        <Text variant="text-lg/leading-normal/medium" lg="text-xl/leading-normal/medium">Vindklassificering</Text>
                        <Text variant="text-base/leading-normal/normal" className='text-pebble-500'>Vindhastighet i meter per sekund (m/s)</Text>

                        <BoxRadius className='lg:hidden flex flex-col bg-white mt-4 px-4'>
                            {DFRContext.windSpeeds.map((s, index) => {
                                return <div key={index} className={`py-4 ${index !== DFRContext.windSpeeds.length - 1 ? 'border-b border-pebble-50' : ''}`}>
                                    <div className='flex items-start gap-4'>
                                        <div className='w-5/12 min-w-0'>
                                            <div className='flex items-center gap-2'>
                                                <div className='rounded-full shrink-0' style={{backgroundColor: s.color, height: '20px', width: '20px', display: 'inline-block'}}></div>
                                                <Text variant="text-base/leading-normal/medium">{s.name} m/s</Text>
                                            </div>
                                        </div>

                                        <div className='w-7/12 min-w-0'>
                                            <div>
                                                <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Kategori</Text>
                                                <Text variant="text-base/leading-normal/medium" className='text-pebble-500'>{s.category}</Text>
                                            </div>

                                            <div className='mt-3'>
                                                <Text variant="text-xs/leading-normal/medium" className="text-pebble-500">Beskrivning</Text>
                                                <Text variant="text-base/leading-normal/medium" className='text-pebble-500'>{s.description}</Text>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            })}
                        </BoxRadius>

                        <BoxRadius className='hidden lg:flex lg:flex-col flex-grow bg-white py-4 px-3 mt-4'>
                            <div className='flex justify-between border-b border-pebble-50 px-2 py-3'>
                                <div className='w-5/12 pr-1'>
                                    <Text variant="text-base/leading-normal/medium">Vindstyrka</Text>
                                </div>

                                <div className='w-1/4 pr-1'>
                                    <Text variant="text-base/leading-normal/medium">Kategori</Text>
                                </div>

                                <div className='w-1/3'>
                                    <Text variant="text-base/leading-normal/medium">Beskrivning</Text>
                                </div>
                            </div>

                            {DFRContext.windSpeeds.map((s, index) => {
                                return <div key={index} className='flex items-center justify-between border-b border-pebble-50 px-2 py-3'>
                                    <div className='flex items-center w-5/12 pr-1'>
                                        <div className='rounded rounded-full mr-2' style={{backgroundColor: s.color, height: '20px', width: '20px', display: 'inline-block'}}></div>
                                        <Text variant="text-base/leading-normal/medium">{s.name} m/s</Text>
                                    </div>

                                    <div className='w-1/4 pr-1'>
                                        <Text variant="text-base/leading-normal/medium" className='text-pebble-500'>{s.category}</Text>
                                    </div>

                                    <div className='w-1/3'>
                                        <Text variant="text-base/leading-normal/medium" className='text-pebble-500'>{s.description}</Text>
                                    </div>
                                </div>
                            })}
                        </BoxRadius>
                    </div>
                </div>
            </BoxRadius>
            
            
        </>
    }

    return <>
        <div className='flex flex-wrap'>
            <div className='w-full'>
                <Text variant="text-xl/leading-normal/medium" lg="text-2xl/leading-normal/medium" className='mb-1'>Vindanalys</Text>

                <Text variant="text-lg/leading-normal/normal" className='mb-2'>En vindros visar hur det vanligtvis blåser på en plats, både riktning och styrka, och för dig som skogsägare är det ett värdefullt verktyg för att förstå vindens påverkan på din skog.</Text>

                <Text variant="text-lg/leading-normal/normal">En vindros hjälper dig förstå din skogs riskområden och var det kan behövas extra tillsyn. Dessutom hjälper den dig att fatta beslut som stärker din skogs långsiktiga stabilitet och minskar riskerna för skador.</Text>
            </div>
        </div>
        
        {dfrActiveProps.map((p, index) => <div key={p.ref || index}>{propertyData(p)}</div>)}

        <BoxRadius className='bg-stone-50 p-6 mb-6'>
            <div className='flex flex-wrap'>
                <div className='w-full'>
                    <Text variant="text-xl/leading-normal/medium" lg="text-2xl/leading-normal/medium" className='mb-4'>Vad innebär det för dig som skogsägare?</Text>

                    <BulletPointList>
                        <li className='text-pebble-500'>
                            <Text variant="text-base/leading-normal/normal">Träden i din skog är över tid “tränade” för att stå emot vanligaste vindriktningen.</Text>
                        </li>

                        <li className='text-pebble-500'>
                            <Text variant="text-base/leading-normal/normal">När vinden däremot kommer från ovanliga riktningar (till exempel från nordost) blir det mer utsatta.</Text>
                        </li>

                        <li className='text-pebble-500'>
                            <Text variant="text-base/leading-normal/normal">Vår analys visar att stormar från nordost historiskt har lett till ökad en frekvens av vindfällen i din region.</Text>
                        </li>
                    </BulletPointList>

                    <Text variant="text-base/leading-normal/normal" className='text-pebble-500'>Vi rekommenderar att du särskilt kontrollerar kanter mot hyggen och gallrade bestånd som vetter mot nordost. Här är träden mest sårbara. Att i förväg identifiera dessa riskzoner gör det möjligt att minimera skador vid nästa storm.</Text>
                </div>
            </div>
        </BoxRadius>
    </>
}
